import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { WordDTO } from "@shared/types";
import { useLocalProfile } from "../local/useLocalProfile";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { AvatarPreview } from "../components/AvatarEditor";
import { GameImage } from "../game/ui/GameImage";
import { sfx } from "../game/audio";
import { useQuizRoom } from "../quizzle/useQuizRoom";
import { buildQuestion } from "../quizzle/questionEngine";
import { QuizQuestion } from "../quizzle/QuizQuestion";
import type { QuizJoinOpts } from "../quizzle/types";
import type { AvatarConfig } from "@shared/types";

interface Props {
  /** When embedded in a class, the class supplies the room code + identity
   *  (the class studentId, so a rejoin restores this student's score). */
  codeOverride?: string;
  identity?: { id: string; displayName: string; avatarConfig: AvatarConfig };
  embedded?: boolean;
}

/** Quizzle Controller — a student's iPad: join, answer, see feedback. */
export function QuizzleController({ codeOverride, identity, embedded }: Props = {}) {
  const nav = useNavigate();
  const { profile } = useLocalProfile();
  const [params] = useSearchParams();
  const code = (codeOverride ?? params.get("code") ?? "").toUpperCase();

  const myIdentity = identity ?? (profile ? { id: getBattlePlayerId(profile.id, code, "join"), displayName: profile.displayName, avatarConfig: profile.avatarConfig } : null);
  const meId = myIdentity?.id;
  const joinOpts = useMemo<QuizJoinOpts | null>(
    () => (myIdentity && code ? { code, role: "controller", me: myIdentity } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meId, code],
  );
  const { client, state, error } = useQuizRoom(joinOpts);

  const question = useMemo(() => {
    if (!state?.pool || (state.phase !== "question" && state.phase !== "reveal")) return null;
    return buildQuestion(state.pool as WordDTO[], state.seed, state.questionIndex, state.settings.enabledTypes);
  }, [state?.pool, state?.seed, state?.questionIndex, state?.phase, state?.settings.enabledTypes]);

  // Play a right/wrong sound on this device when the answer is revealed.
  useEffect(() => {
    if (state?.phase !== "reveal") return;
    const mine = state.players.find((p) => p.id === meId);
    if ((mine?.lastDelta ?? 0) > 0) sfx.correct(); else sfx.wrong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.phase, state?.questionIndex]);

  // Standalone only: bounce to menu if there's no profile. Embedded-in-class
  // uses the class identity, so no profile is required.
  useEffect(() => { if (!identity && !profile) nav("/", { replace: true }); }, [identity, profile, nav]);
  if (!identity && !profile) return null;

  if (!code) {
    return <div className="quizzle-ctrl"><p className="quizzle-error">No room code.</p>{!embedded && <button className="btn btn-green" onClick={() => nav("/quizzle")}>Enter a code</button>}</div>;
  }
  if (error) {
    return <div className="quizzle-ctrl"><p className="quizzle-error">⚠️ {error}</p>{!embedded && <button className="btn btn-ghost" onClick={() => nav("/quizzle")}>← Back</button>}</div>;
  }

  const me = state?.players.find((p) => p.id === meId);
  const ranked = [...(state?.players ?? [])].sort((a, b) => b.score - a.score);
  const myRank = me ? ranked.findIndex((p) => p.id === me.id) + 1 : 0;

  // QUESTION — answer UI.
  if (state?.phase === "question" && question && state.questionStartedAt) {
    return (
      <div className="quizzle-ctrl quizzle-ctrl-q">
        <QuizQuestion
          key={state.questionIndex}
          question={question}
          questionStartedAt={state.questionStartedAt}
          onAnswer={(correct, elapsedMs) => client?.answer(correct, elapsedMs)}
        />
      </div>
    );
  }

  // REVEAL — correctness + re-teach + rank.
  if (state?.phase === "reveal" && question) {
    const gotIt = (me?.lastDelta ?? 0) > 0;
    return (
      <div className={`quizzle-ctrl quizzle-ctrl-reveal ${gotIt ? "is-right" : "is-wrong"}`}>
        <h1>{gotIt ? "✓ Correct!" : "✗ Not this time"}</h1>
        {gotIt && <div className="quizzle-ctrl-plus">+{me?.lastDelta}</div>}
        <div className="quizzle-reteach">
          <GameImage className="quizzle-reteach-img" src={question.word.imageUrl} alt="" fallbackLabel="?" />
          <strong className="quizzle-reteach-word">{question.word.english}</strong>
          {question.word.vietnamese && <small>{question.word.vietnamese}</small>}
        </div>
        <p className="quizzle-dim">Score {me?.score ?? 0} · Rank #{myRank}</p>
      </div>
    );
  }

  // PODIUM — final placement. Stay connected; the host can start another
  // round, which drops everyone back to the lobby automatically.
  if (state?.phase === "podium") {
    return (
      <div className="quizzle-ctrl quizzle-ctrl-podium">
        <div className="quizzle-ctrl-rank">#{myRank}</div>
        <h1>{myRank === 1 ? "🏆 You won!" : myRank <= 3 ? "🎉 Podium!" : "Good game!"}</h1>
        <p className="quizzle-dim">Final score {me?.score ?? 0} · {ranked.length} players</p>
        <p className="quizzle-dim">Stay here — the host can start another round!</p>
      </div>
    );
  }

  // LOBBY / waiting.
  return (
    <div className="quizzle-ctrl">
      <div className="quizzle-ctrl-joined">
        <span className="quizzle-ctrl-avatar"><AvatarPreview cfg={myIdentity?.avatarConfig ?? {}} /></span>
        <h1>You're in!</h1>
        <p className="quizzle-dim">{state?.players.length ?? 0} players</p>
        <p className="quizzle-dim">Look up at the big screen — the quiz starts when the host is ready.</p>
      </div>
      {!embedded && <button className="btn btn-ghost" onClick={() => nav("/quizzle")}>Leave</button>}
    </div>
  );
}
